import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from '@fullcalendar/daygrid';
import { useEffect, useRef, useState } from "react";
import listPlugin from "@fullcalendar/list";
import multiMonthPlugin from "@fullcalendar/multimonth";
import { getFormEntriesByFormName } from "../services/formServices";
import { FORMNAMES, setBrowserTitle } from "../utilities/codes";
import toast from "react-hot-toast";
import { AnimatePresence, motion } from "framer-motion";
import { I18N_KEYS } from "../languages/PlaceHolder";
import { useTranslation } from "react-i18next";

const Calendar = () => {
    const { t } = useTranslation();
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [events, setEvents] = useState([]);
    const [dateColorMap, setDateColorMap] = useState({});
    const calendarRef = useRef(null);

    useEffect(() => {
        setBrowserTitle("Calendar")
        getEvents();
    }, [])

    const getEvents = async () => {
        try {
            const response = await getFormEntriesByFormName(FORMNAMES.CALENDAREVENTS);
            if (response && response.length > 0) {
                const res = response.map((e) => ({
                    ...e.payload,
                    allDay: true,
                    extendedProps: { ...e.payload },
                }));
                const normalizeDate = (d) =>
                    new Date(d).toISOString().split('T')[0];
                const bgColor = Object.fromEntries(
                    res
                        .filter(e => e?.extendedProps?.start && e?.extendedProps?.bgColor)
                        .map(e => [
                            normalizeDate(e.extendedProps.start),
                            e.extendedProps.bgColor,
                        ])
                );
                setDateColorMap(bgColor);
                const visibleEvents = res.filter(e => e?.title);
                setEvents(visibleEvents);
            }
        } catch (error) {
            toast.error(error.message);
        }
    };

    return (<>
        <div className="common-container p-6">
            <FullCalendar
                key={JSON.stringify(dateColorMap)}
                ref={calendarRef}
                plugins={[dayGridPlugin, listPlugin, multiMonthPlugin]}
                initialView="dayGridMonth"
                events={events}
                headerToolbar={{
                    left: 'prev,next today',
                    center: 'title',
                    right: 'dayGridMonth,multiMonthYear,allList,myEvents,listYear',
                }}
                customButtons={{
                    allList: {
                        text: "Month Events",
                        click: () => {
                            const calendarApi = calendarRef.current.getApi();
                            calendarApi.removeAllEventSources();
                            calendarApi.addEventSource(events);
                            calendarApi.changeView("listMonth");
                        },
                    },
                    myEvents: {
                        text: 'Birthday',
                        click: () => {
                            const calendarApi = calendarRef.current.getApi();
                            calendarApi.removeAllEventSources();
                            calendarApi.addEventSource(events?.filter(ev => ev?.event_type === "Birthday"));
                            calendarApi.changeView("listMonth");
                        },
                    },
                }}
                buttonText={{
                    today: 'Today',
                    dayGridMonth: 'Month',
                    listYear: 'All Events',
                    year: 'Calendar',
                }}
                viewDidMount={(arg) => {
                    const todayBtn = document.querySelector('.fc-today-button');
                    if (!todayBtn) return;

                    const hide =
                        arg.view.type === 'listYear' ||
                        arg.view.type === 'multiMonthYear' ||
                        arg.view.type === 'listMonth';

                    todayBtn.style.display = hide ? 'none' : 'inline-flex';
                }}
                eventClick={(info) => {
                    const start = info?.event?.start;
                    const end = info?.event?.end;
                    let formattedDate = start.toLocaleDateString("en-GB");
                    if (end) {
                        const endDate = new Date(end);
                        endDate.setDate(endDate?.getDate() - 1);

                        formattedDate = `${formattedDate} - ${endDate.toLocaleDateString("en-GB")}`;
                    }
                    setSelectedEvent({
                        title: info?.event?.title,
                        date: formattedDate,
                        description: info?.event?.extendedProps?.description,
                    });
                }}
                dayCellDidMount={(info) => {
                    const date = info.date;
                    const dateKey = date.toISOString().slice(0, 10)
                    const bgColor = dateColorMap[dateKey];
                    if (bgColor) {
                        info.el.style.backgroundColor = bgColor;
                    }
                }}
            />
            {selectedEvent && (
                <AnimatePresence>
                    <motion.div
                        className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-100"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setSelectedEvent(null)}
                    >
                        <button
                            onClick={() => setSelectedEvent(null)}
                            className="absolute top-6 right-6 bg-accent rounded-full w-10 h-10 flex items-center justify-center shadow-md text-xl font-bold hover:bg-red-500 hover:text-white transition"
                        >
                            &times;
                        </button>

                        <motion.div
                            initial={{ opacity: 0, y: 50, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 30, scale: 0.95 }}
                            transition={{ duration: 0.35, ease: "easeOut" }}
                            onClick={(e) => e.stopPropagation()}
                            className="relative w-full max-w-lg max-h-[90vh] bg-accent rounded-2xl shadow-2xl p-6 overflow-y-auto"
                        >
                            {selectedEvent?.title && (
                                <h2 className="text-3xl font-extrabold text-primary text-center mb-2">
                                    {selectedEvent?.title}
                                </h2>
                            )}

                            <div className="w-20 h-1 bg-gradient-to-r from-orange-400 via-yellow-300 to-orange-400 rounded-full mx-auto mb-4" />

                            {selectedEvent?.date && (
                                <p className="text-center text-sm text-primary mb-4">
                                    {t(I18N_KEYS.DATE)}: {selectedEvent?.date}
                                </p>
                            )}

                            {selectedEvent?.description && (
                                <div
                                    className="text-primary space-y-2"
                                    dangerouslySetInnerHTML={{ __html: selectedEvent?.description }}
                                />
                            )}
                        </motion.div>
                    </motion.div>
                </AnimatePresence>
            )}
        </div>
    </>)
};


export default Calendar;

// https://fullcalendar.io/docs/event-object